/**
 * Email: whitepaper SES sends omit ConfigurationSetName (like contact/crisis) so links
 * stay on pelgo.com and are not rewritten to `*.awstrack.me`.
 */
import { Button } from '@/components/ui/button'
import apiClient from '@/lib/api-client'
import { getFeaturedWhitepaper, getWhitepaperLandingPath } from '@/lib/whitepapers'
import type { ApiError } from '@/types'
import { Download, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

export function WhitepaperDownloadPage() {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const landingPath = slug
    ? getWhitepaperLandingPath(slug)
    : getWhitepaperLandingPath(getFeaturedWhitepaper().slug)
  const [status, setStatus] = useState<'loading' | 'success' | 'expired' | 'error'>('loading')
  const [hasTriggeredAutoDownload, setHasTriggeredAutoDownload] = useState(false)
  const [downloadData, setDownloadData] = useState<{
    download_url: string
    filename: string
    title: string
  } | null>(null)

  useEffect(() => {
    if (!token) {
      setStatus('error')
      return
    }

    apiClient
      .get(`/api/v1/whitepaper/download/${token}`)
      .then((res) => {
        setDownloadData(res.data)
        setStatus('success')
      })
      .catch((err) => {
        const statusCode =
          typeof err === 'object' && err && 'statusCode' in err
            ? (err as ApiError).statusCode
            : undefined

        if (statusCode === 410) {
          setStatus('expired')
        } else {
          setStatus('error')
        }
      })
  }, [token])

  useEffect(() => {
    if (status !== 'success' || !downloadData || hasTriggeredAutoDownload) return
    setHasTriggeredAutoDownload(true)

    try {
      window.location.assign(downloadData.download_url)
    } catch {
      // If navigation is blocked, user can click the manual download button.
    }
  }, [downloadData, hasTriggeredAutoDownload, status])

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center space-y-6">
        {status === 'loading' && (
          <div className="space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#0050e6]" />
            <p className="text-gray-600">Preparing your download...</p>
          </div>
        )}

        {status === 'success' && downloadData && (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
              <Download className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Download Started</h1>
            <p className="text-gray-600">
              Your download of <strong>{downloadData.title}</strong> should begin automatically.
            </p>
            <div className="pt-2 space-y-3">
              <Button
                asChild
                className="bg-[#0050e6] hover:bg-[#0050e6]/90 text-white rounded-full px-6"
              >
                <a href={downloadData.download_url} target="_blank" rel="noopener noreferrer">
                  Download
                </a>
              </Button>
              <p className="text-sm text-gray-500">
                If you have an ad blocker enabled, you may need to allow downloads for this site.
              </p>
            </div>
          </div>
        )}

        {status === 'expired' && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold text-gray-900">Link Expired</h1>
            <p className="text-gray-600">
              This download link has expired. Please request a new one.
            </p>
            <Button
              asChild
              className="bg-[#0050e6] hover:bg-[#0050e6]/90 text-white rounded-full px-6"
            >
              <Link to={landingPath}>Request New Link</Link>
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
            <p className="text-gray-600">We couldn't process your download. Please try again.</p>
            <Button
              asChild
              className="bg-[#0050e6] hover:bg-[#0050e6]/90 text-white rounded-full px-6"
            >
              <Link to={landingPath}>Go Back</Link>
            </Button>
          </div>
        )}

        <div className="pt-8">
          <Link to="/" className="text-sm text-gray-400 hover:text-gray-600">
            pelgo.com
          </Link>
        </div>
      </div>
    </div>
  )
}
